import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  ClockIcon,
  TruckIcon,
  DocumentTextIcon,
  UserIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { odometerService } from '../../services/logistics/odometer';
import LoadingSpinner from '../ui/LoadingSpinner';

const OdometerHistoryModal = ({
  isOpen,
  onClose,
  vehicle
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [readings, setReadings] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (isOpen && vehicle) {
      loadOdometerData();
    }
  }, [isOpen, vehicle]);

  const loadOdometerData = async () => {
    setLoading(true);
    try {
      const [readingsData, statsData] = await Promise.all([
        odometerService.getVehicleReadings(vehicle.companyId, vehicle.id, 100),
        odometerService.getVehicleOdometerStats(vehicle.companyId, vehicle.id)
      ]);

      setReadings(readingsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading odometer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date?.toDate ? date.toDate() : (date instanceof Date ? date : new Date(date));
    return dateObj.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'fuel_import':
        return <TruckIcon className="h-4 w-4 text-blue-600" />;
      case 'maintenance':
        return <DocumentTextIcon className="h-4 w-4 text-orange-600" />;
      case 'manual':
      default:
        return <UserIcon className="h-4 w-4 text-green-600" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'fuel_import':
        return t('vehicles.fuel_import', 'Fuel Import');
      case 'maintenance':
        return t('vehicles.maintenance', 'Maintenance');
      case 'manual':
      default:
        return t('vehicles.manual_entry', 'Manual Entry');
    }
  };

  const calculateDistance = (currentReading, previousReading) => {
    if (!previousReading) return null;
    const distance = currentReading - previousReading;
    return distance > 0 ? distance : null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2" />
            {t('vehicles.odometer_history', 'Odometer History')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {vehicle && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-lg font-medium text-gray-900">
              {vehicle.plateNumber}
            </div>
            <div className="text-sm text-gray-600">
              {vehicle.make} {vehicle.model}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Statistics */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="stats-card">
                  <div className="text-sm text-gray-600">
                    {t('vehicles.total_readings', 'Total Readings')}
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.totalReadings}
                  </div>
                </div>

                <div className="stats-card">
                  <div className="text-sm text-gray-600">
                    {t('vehicles.current_reading', 'Current Reading')}
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {stats.currentReading ? `${stats.currentReading.toLocaleString()} km` : 'N/A'}
                  </div>
                </div>

                <div className="stats-card">
                  <div className="text-sm text-gray-600">
                    {t('vehicles.total_distance', 'Total Distance')}
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.totalDistance ? `${stats.totalDistance.toLocaleString()} km` : 'N/A'}
                  </div>
                </div>

                <div className="stats-card">
                  <div className="text-sm text-gray-600">
                    {t('vehicles.average_distance', 'Avg Distance')}
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {stats.averageDistance ? `${stats.averageDistance} km` : 'N/A'}
                  </div>
                </div>
              </div>
            )}

            {/* Readings Table */}
            <div className="card">
              <div className="card-header">
                <h4 className="text-md font-medium text-gray-900">
                  {t('vehicles.reading_history', 'Reading History')}
                </h4>
              </div>
              <div className="card-body">
                {readings.length === 0 ? (
                  <div className="text-center py-12">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {t('vehicles.no_readings_found', 'No odometer readings found')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('vehicles.readings_will_appear_here', 'Odometer readings will appear here')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="table-header">{t('vehicles.date_time', 'Date & Time')}</th>
                          <th className="table-header">{t('vehicles.reading', 'Reading')}</th>
                          <th className="table-header">{t('vehicles.distance', 'Distance')}</th>
                          <th className="table-header">{t('vehicles.source', 'Source')}</th>
                          <th className="table-header">{t('vehicles.recorded_by', 'Recorded By')}</th>
                          <th className="table-header">{t('vehicles.notes', 'Notes')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {readings.map((reading, index) => {
                          const previousReading = index < readings.length - 1 ? readings[index + 1].reading : null;
                          const distance = calculateDistance(reading.reading, previousReading);

                          return (
                            <tr key={reading.id} className="hover:bg-gray-50">
                              <td className="table-cell">
                                <div className="text-sm text-gray-900">
                                  {formatDate(reading.recordedAt)}
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm font-medium text-gray-900">
                                  {reading.reading.toLocaleString()} km
                                </div>
                              </td>
                              <td className="table-cell">
                                {distance ? (
                                  <div className="text-sm text-green-600 font-medium">
                                    +{distance.toLocaleString()} km
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-400">-</div>
                                )}
                              </td>
                              <td className="table-cell">
                                <div className="flex items-center">
                                  {getSourceIcon(reading.source)}
                                  <span className="ml-2 text-sm text-gray-900">
                                    {getSourceLabel(reading.source)}
                                  </span>
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm text-gray-600">
                                  {reading.recordedBy === 'system' ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                      Auto-sync
                                    </span>
                                  ) : (
                                    reading.recordedBy || 'Unknown'
                                  )}
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm text-gray-600 max-w-xs truncate" title={reading.notes}>
                                  {reading.notes || '-'}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OdometerHistoryModal;